import { BookOpen } from "lucide-react";
import { EmptyState, ErrorBanner, LoadingText, Pagination, SearchInput } from "../ui";
import PublicReportCard from "./PublicReportCard";
import type { PublicReportPage } from "../../types/reports";

type DiscoverPanelProps = {
  page: PublicReportPage | null;
  loading: boolean;
  error: string;
  search: string;
  onSearchChange: (value: string) => void;
  currentPage: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  onSubscriptionChange: () => void;
};

export default function DiscoverPanel({
  page,
  loading,
  error,
  search,
  onSearchChange,
  currentPage,
  onPageChange,
  pageSize,
  onSubscriptionChange,
}: DiscoverPanelProps) {
  const items = page?.items ?? [];

  return (
    <>
      <div className="mb-4 max-w-lg">
        <SearchInput
          id="discover-reports-search"
          label="Search public reports"
          value={search}
          onChange={onSearchChange}
          placeholder="Search by report, description, or creator..."
        />
      </div>

      {loading ? (
        <LoadingText>Loading public reports...</LoadingText>
      ) : error ? (
        <ErrorBanner>{error}</ErrorBanner>
      ) : items.length === 0 ? (
        search ? (
          <EmptyState
            bordered
            icon={<BookOpen size={32} aria-hidden="true" />}
            title="No reports match your search"
            description="Try another term or clear the search."
            action={
              <button
                onClick={() => onSearchChange("")}
                className="text-sm font-medium text-blue-600 hover:underline"
              >
                Clear search
              </button>
            }
          />
        ) : (
          <EmptyState
            bordered
            icon={<BookOpen size={32} aria-hidden="true" />}
            title="No public reports yet"
            description="Public reports created by Brian users will appear here."
          />
        )
      ) : (
        <>
          <div className="space-y-3">
            {items.map((report) => (
              <PublicReportCard
                key={report.id}
                report={report}
                onSubscriptionChange={onSubscriptionChange}
              />
            ))}
          </div>
          <Pagination
            page={currentPage}
            pageSize={pageSize}
            total={page?.total ?? 0}
            onPageChange={onPageChange}
            label="Public report pages"
          />
        </>
      )}
    </>
  );
}
